import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CatalogListing, CatalogListingInput, createCatalogListing, updateCatalogListing } from '@/services/catalog';
import { fetchLaptops } from '@/services/laptops';
import { fetchAccessories } from '@/services/accessories';
import { groupLaptops, GroupedLaptop } from '@/lib/laptopGrouping';
import { Modal } from '@/components/common/Modal';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { parseDatabaseError } from '@/lib/errors';
import {
  Laptop as LaptopIcon,
  Package,
  Sparkles,
  Loader2,
  Check,
} from 'lucide-react';

interface CatalogListingModalProps {
  isOpen: boolean;
  listing?: CatalogListing | null;
  onClose: () => void;
  onSuccess: () => void;
}

function generateSlug(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export const CatalogListingModal: React.FC<CatalogListingModalProps> = ({
  isOpen,
  listing,
  onClose,
  onSuccess,
}) => {
  const isEdit = !!listing;

  const [listingType, setListingType] = useState<'laptop_group' | 'accessory'>(
    listing?.listing_type || 'laptop_group'
  );
  const [title, setTitle] = useState(listing?.title || '');
  const [slug, setSlug] = useState(listing?.slug || '');
  const [description, setDescription] = useState(listing?.description || '');
  const [coverImagePath, setCoverImagePath] = useState(listing?.cover_image_path || '');
  const [isFeatured, setIsFeatured] = useState(listing?.is_featured ?? false);
  const [isPublished, setIsPublished] = useState(listing?.is_published ?? true);
  const [showOutOfStock, setShowOutOfStock] = useState(listing?.show_out_of_stock ?? false);
  const [sortOrder, setSortOrder] = useState(listing?.sort_order ?? 0);
  const [badge, setBadge] = useState(listing?.badge || '');

  // Laptop Group specific
  const [brand, setBrand] = useState(listing?.brand || '');
  const [model, setModel] = useState(listing?.model || '');
  const [cpu, setCpu] = useState(listing?.cpu || '');
  const [touchscreen, setTouchscreen] = useState<boolean | null>(listing?.touchscreen ?? null);

  // Accessory specific
  const [accessoryId, setAccessoryId] = useState<string | null>(listing?.accessory_id || null);

  const [displayPriceOverride, setDisplayPriceOverride] = useState<string>(
    listing?.display_price_override != null ? String(listing.display_price_override) : ''
  );
  const [originalPrice, setOriginalPrice] = useState<string>(
    listing?.original_price != null ? String(listing.original_price) : ''
  );

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch available laptops for auto-selection
  const { data: laptopsData } = useQuery({
    queryKey: ['laptops', 'catalog-builder'],
    queryFn: () => fetchLaptops({ status: 'in_stock', fetchAll: true }),
    enabled: isOpen,
  });

  const availableLaptops = laptopsData?.data ?? [];
  const laptopGroups = useMemo(() => groupLaptops(availableLaptops), [availableLaptops]);

  // Fetch accessories for auto-selection
  const { data: accessoriesData } = useQuery({
    queryKey: ['accessories', 'catalog-builder'],
    queryFn: () => fetchAccessories({ page: 0 }),
    enabled: isOpen,
  });
  const availableAccessories = accessoriesData?.data ?? [];

  // Reset or populate on open
  useEffect(() => {
    if (listing) {
      setListingType(listing.listing_type);
      setTitle(listing.title);
      setSlug(listing.slug);
      setDescription(listing.description || '');
      setCoverImagePath(listing.cover_image_path || '');
      setIsFeatured(listing.is_featured);
      setIsPublished(listing.is_published);
      setShowOutOfStock(listing.show_out_of_stock);
      setSortOrder(listing.sort_order);
      setBadge(listing.badge || '');
      setBrand(listing.brand || '');
      setModel(listing.model || '');
      setCpu(listing.cpu || '');
      setTouchscreen(listing.touchscreen);
      setAccessoryId(listing.accessory_id);
      setDisplayPriceOverride(listing.display_price_override != null ? String(listing.display_price_override) : '');
      setOriginalPrice(listing.original_price != null ? String(listing.original_price) : '');
    } else {
      setListingType('laptop_group');
      setTitle('');
      setSlug('');
      setDescription('');
      setCoverImagePath('');
      setIsFeatured(false);
      setIsPublished(true);
      setShowOutOfStock(false);
      setSortOrder(0);
      setBadge('');
      setBrand('');
      setModel('');
      setCpu('');
      setTouchscreen(null);
      setAccessoryId(null);
      setDisplayPriceOverride('');
      setOriginalPrice('');
    }
    setError(null);
  }, [listing, isOpen]);

  const handleSelectLaptopGroup = (group: GroupedLaptop) => {
    setBrand(group.brand);
    setModel(group.model);
    setCpu(group.cpu || '');
    setTouchscreen(group.touchscreen ?? null);

    const generatedTitle = `${group.brand} ${group.model}${group.cpu ? ` (${group.cpu})` : ''}${
      group.touchscreen === true ? ' Touch' : ''
    }`;
    setTitle(generatedTitle);
    setSlug(generateSlug(generatedTitle));
  };

  const handleSelectAccessory = (acc: any) => {
    setAccessoryId(acc.id);
    setBrand(acc.brand || '');
    setModel(acc.model || '');
    setTitle(acc.name);
    setSlug(generateSlug(acc.name));
    if (acc.selling_price) {
      setDisplayPriceOverride(String(acc.selling_price));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    if (!slug.trim()) {
      setError('URL slug is required.');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const payload: CatalogListingInput = {
        listing_type: listingType,
        slug: generateSlug(slug),
        title: title.trim(),
        description: description.trim() || null,
        cover_image_path: coverImagePath.trim() || null,
        is_featured: isFeatured,
        is_published: isPublished,
        show_out_of_stock: showOutOfStock,
        sort_order: Number(sortOrder) || 0,
        badge: badge.trim() || null,
        brand: brand.trim() || null,
        model: model.trim() || null,
        cpu: cpu.trim() || null,
        touchscreen: touchscreen,
        accessory_id: listingType === 'accessory' ? accessoryId : null,
        display_price_override: displayPriceOverride ? Number(displayPriceOverride) : null,
        original_price: originalPrice ? Number(originalPrice) : null,
      };

      if (isEdit && listing) {
        await updateCatalogListing(listing.id, payload);
      } else {
        await createCatalogListing(payload);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(parseDatabaseError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      title={isEdit ? `Edit Catalog Listing — ${listing?.title}` : 'Create Public Catalog Listing'}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="space-y-4 text-xs max-h-[75vh] overflow-y-auto pr-1">
        {error && <ErrorAlert message={error} />}

        {/* Listing Type Toggle */}
        {!isEdit && (
          <div className="flex rounded-lg border border-brand-border p-1 bg-brand-bg">
            <button
              type="button"
              onClick={() => setListingType('laptop_group')}
              className={`flex-1 py-1.5 font-bold rounded-md flex items-center justify-center space-x-1.5 transition-colors ${
                listingType === 'laptop_group'
                  ? 'bg-white text-brand-black shadow-xs'
                  : 'text-brand-muted hover:text-brand-black'
              }`}
            >
              <LaptopIcon className="w-3.5 h-3.5 text-brand-green" />
              <span>Laptop Model Group</span>
            </button>
            <button
              type="button"
              onClick={() => setListingType('accessory')}
              className={`flex-1 py-1.5 font-bold rounded-md flex items-center justify-center space-x-1.5 transition-colors ${
                listingType === 'accessory'
                  ? 'bg-white text-brand-black shadow-xs'
                  : 'text-brand-muted hover:text-brand-black'
              }`}
            >
              <Package className="w-3.5 h-3.5 text-blue-600" />
              <span>Accessory / Part</span>
            </button>
          </div>
        )}

        {/* Quick Autofill from Inventory */}
        {!isEdit && (
          <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-lg space-y-2">
            <div className="flex items-center space-x-1.5 font-bold text-blue-950">
              <Sparkles className="w-3.5 h-3.5 text-blue-600" />
              <span>Autofill from Current Live Inventory</span>
            </div>

            {listingType === 'laptop_group' ? (
              <div>
                <label className="block text-[10px] font-semibold text-blue-800 uppercase mb-1">
                  Select Base Laptop Group
                </label>
                <select
                  onChange={(e) => {
                    const group = laptopGroups.find((g) => g.key === e.target.value);
                    if (group) handleSelectLaptopGroup(group);
                  }}
                  className="w-full bg-white border border-blue-200 rounded px-2 py-1.5 text-xs font-medium"
                >
                  <option value="">-- Choose from {laptopGroups.length} available groups --</option>
                  {laptopGroups.map((g) => (
                    <option key={g.key} value={g.key}>
                      {g.brand} {g.model} ({g.cpu || 'Base CPU'}) • {g.inStockQuantity} in stock
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="block text-[10px] font-semibold text-blue-800 uppercase mb-1">
                  Select Accessory
                </label>
                <select
                  onChange={(e) => {
                    const acc = availableAccessories.find((a) => a.id === e.target.value);
                    if (acc) handleSelectAccessory(acc);
                  }}
                  className="w-full bg-white border border-blue-200 rounded px-2 py-1.5 text-xs font-medium"
                >
                  <option value="">-- Choose from accessories --</option>
                  {availableAccessories.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} (SKU: {a.sku}) • {a.quantity} in stock
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* Core Listing Details */}
        <div className="space-y-3">
          <div>
            <label className="block font-bold text-brand-black mb-1">
              Public Listing Title <span className="text-brand-red">*</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!isEdit && !slug) setSlug(generateSlug(e.target.value));
              }}
              placeholder="e.g. Dell Latitude 7490 Core i7 8th Gen Touchscreen"
              className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md focus:ring-2 focus:ring-brand-green font-medium"
              required
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block font-bold text-brand-black mb-1">
                URL Slug <span className="text-brand-red">*</span>
              </label>
              <div className="flex items-center">
                <span className="px-2 py-2 bg-slate-100 border border-r-0 border-brand-border rounded-l text-[11px] text-brand-muted">
                  /shop/
                </span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(generateSlug(e.target.value))}
                  placeholder="dell-latitude-7490-touch"
                  className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-r-md focus:ring-2 focus:ring-brand-green font-mono text-xs"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block font-bold text-brand-black mb-1">Badge Tag</label>
              <input
                type="text"
                value={badge}
                onChange={(e) => setBadge(e.target.value)}
                placeholder="e.g. Featured, Best Seller, New Arrival, 20% OFF"
                className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md focus:ring-2 focus:ring-brand-green"
              />
            </div>
          </div>

          {/* Pricing Customization */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3 bg-brand-bg rounded-lg border border-brand-border">
            <div>
              <label className="block font-bold text-brand-black mb-1">
                Fixed Display Price Override (PKR)
              </label>
              <input
                type="number"
                value={displayPriceOverride}
                onChange={(e) => setDisplayPriceOverride(e.target.value)}
                placeholder="Leave blank to use live stock asking price"
                className="w-full px-3 py-1.5 bg-white border border-brand-border rounded-md font-mono"
              />
              <span className="text-[10px] text-brand-muted mt-0.5 block">
                Optional: locks display price instead of computing from stock range.
              </span>
            </div>

            <div>
              <label className="block font-bold text-brand-black mb-1">
                Original / Strikethrough Price (PKR)
              </label>
              <input
                type="number"
                value={originalPrice}
                onChange={(e) => setOriginalPrice(e.target.value)}
                placeholder="e.g. 75000 (shows crossed out)"
                className="w-full px-3 py-1.5 bg-white border border-brand-border rounded-md font-mono"
              />
              <span className="text-[10px] text-brand-muted mt-0.5 block">
                Optional: shows discount relative to original MSRP.
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block font-bold text-brand-black mb-1">Public Description & Features</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="e.g. Premium ultrabook with high battery life, backlit keyboard, original charger included..."
              className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md focus:ring-2 focus:ring-brand-green resize-none"
            />
          </div>

          {/* Cover Image URL */}
          <div>
            <label className="block font-bold text-brand-black mb-1">Cover Image URL</label>
            <input
              type="url"
              value={coverImagePath}
              onChange={(e) => setCoverImagePath(e.target.value)}
              placeholder="https://images.unsplash.com/... or /images/laptop.jpg"
              className="w-full px-3 py-2 bg-brand-bg border border-brand-border rounded-md focus:ring-2 focus:ring-brand-green"
            />
          </div>

          {/* Visibility and Sort Settings */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-brand-border">
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isPublished}
                onChange={(e) => setIsPublished(e.target.checked)}
                className="rounded border-brand-border accent-brand-green"
              />
              <span className="font-semibold text-brand-black">Published</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isFeatured}
                onChange={(e) => setIsFeatured(e.target.checked)}
                className="rounded border-brand-border accent-brand-green"
              />
              <span className="font-semibold text-brand-black">Featured on Top</span>
            </label>

            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showOutOfStock}
                onChange={(e) => setShowOutOfStock(e.target.checked)}
                className="rounded border-brand-border accent-brand-green"
              />
              <span className="font-semibold text-brand-black">Show When Out of Stock</span>
            </label>

            <div>
              <label className="block text-[10px] font-bold text-brand-muted uppercase">
                Sort Priority
              </label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                className="w-full px-2 py-1 bg-brand-bg border border-brand-border rounded font-mono text-center"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-2 pt-3 border-t border-brand-border">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-xs font-semibold text-brand-muted hover:text-brand-black"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center space-x-1.5 px-5 py-2 bg-brand-green text-white text-xs font-bold rounded-md hover:bg-brand-green-hover transition-colors shadow-sm disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            <span>{isEdit ? 'Update Listing' : 'Publish Listing'}</span>
          </button>
        </div>
      </form>
    </Modal>
  );
};
